import { SCHEMA_DATA_TYPES } from "@/types/schemaBuilder";
import type {
  SchemaActivityEvent,
  SchemaColumn,
  SchemaDetail,
  SchemaRule,
  SchemaRulePreset,
  SchemaTemplate,
} from "@/types/schemaBuilder";

export const mockSchemas: SchemaDetail[] = [
  {
    id: "schema-draft",
    name: "Sample Schema Name",
    description:
      "Description of the schema and its general use as far as the client/organization is concerned",
    status: "draft",
    totalColumns: 55,
    rules: 31,
    conflicts: 0,
    lastEditedAt: "February 2, 2026",
    lastEditor: "Bharti Devi",
    createdAt: "January 13, 2026",
    columns: [],
    mappedFiles: [],
  },
  {
    id: "schema-conflicts",
    name: "Sample Schema Name",
    description:
      "Description of the schema and its general use as far as the client/organization is concerned",
    status: "draft",
    totalColumns: 55,
    rules: 31,
    conflicts: 4,
    lastEditedAt: "February 2, 2026",
    lastEditor: "Bharti Devi",
    createdAt: "January 13, 2026",
    columns: [],
    mappedFiles: [],
  },
  {
    id: "schema-published",
    name: "Lloyds 5.2 updated Final Final v3",
    description:
      "Description of the schema and its general use as far as the client/organization is concerned",
    status: "published",
    totalColumns: 55,
    rules: 31,
    filesRemapped: 72,
    lastUsedAt: "March 30, 2026",
    publishedAt: "February 2, 2026",
    createdAt: "January 13, 2026",
    columns: [],
    mappedFiles: [],
  },
  {
    id: "schema-preset",
    name: "Lloyd's 5.2",
    description:
      "Cover holder reporting standard. Duplicate to use as base template for customization.",
    status: "preset",
    totalColumns: 84,
    rules: 70,
    filesRemapped: 1034,
    lastUsedAt: "March 30, 2026",
    lastUpdatedAt: "February 2, 2026",
    createdAt: "January 13, 2026",
    columns: [],
    mappedFiles: [],
  },
  {
    id: "schema-archived",
    name: "Sample Schema Name",
    description:
      "Description of the schema and its general use as far as the client/organization is concerned",
    status: "archived",
    totalColumns: 55,
    rules: 31,
    filesRemapped: 72,
    lastUsedAt: "March 30, 2026",
    publishedAt: "February 2, 2026",
    createdAt: "January 13, 2026",
    columns: [],
    mappedFiles: [],
  },
];

export const mockSchemaColumns: SchemaColumn[] = [
  {
    fieldId: "field-policy-number",
    id: "1",
    sortOrder: 1,
    name: "Policy_Number",
    description: "",
    dataType: "General",
    formatting: "---",
    example: "AAAABB12345",
    mandatory: true,
    unique: true,
  },
  {
    fieldId: "field-total-loss",
    id: "2",
    sortOrder: 2,
    name: "Total_Loss",
    description: "",
    dataType: "Decimal",
    formatting: "---",
    example: "1204.49",
    mandatory: false,
    unique: false,
  },
  {
    fieldId: "field-occupancy-description",
    id: "3",
    sortOrder: 3,
    name: "Occupancy_Description",
    description: "",
    dataType: "General",
    formatting: "---",
    example: "Tenanted",
    mandatory: false,
    unique: false,
  },
  {
    fieldId: "field-inception-date",
    id: "4",
    sortOrder: 4,
    name: "Inception_Date",
    description: "",
    dataType: "Date & Time",
    formatting: "DD/MM/YYYY",
    example: "03/01/2026",
    mandatory: false,
    unique: false,
  },
  {
    fieldId: "field-expiration-date",
    id: "5",
    sortOrder: 5,
    name: "Expiration_Date",
    description: "",
    dataType: "Date & Time",
    formatting: "DD/MM/YYYY",
    example: "09/28/2027",
    mandatory: false,
    unique: false,
  },
  {
    fieldId: "field-claim-count",
    id: "6",
    sortOrder: 6,
    name: "Claim_Count",
    description: "",
    dataType: "Integer",
    formatting: "---",
    example: "12",
    mandatory: false,
    unique: false,
  },
  {
    fieldId: "field-suspected-fraud",
    id: "7",
    sortOrder: 7,
    name: "Suspected_Fraud",
    description: "",
    dataType: "Boolean",
    formatting: "1, 0",
    example: "1",
    mandatory: false,
    unique: false,
  },
];

export const mockRulePresets: SchemaRulePreset[] = [
  // ── Syntactical ──────────────────────────────────────────────
  {
    id: "mandatory",
    backendRuleType: "mandatory",
    scope: "column",
    title: "Mandatory (not null / empty)",
    category: "Syntactical",
    description: "Value must be present.",
    expression: "[{col}] must not be null or empty",
    applicableDataTypes: SCHEMA_DATA_TYPES,
    defaultCriticality: "error",
  },
  {
    id: "column_unique",
    backendRuleType: "column_unique",
    scope: "column",
    title: "No duplicate values",
    category: "Syntactical",
    description: "All values in the column must be unique.",
    expression: "[{col}] must be a unique value",
    applicableDataTypes: SCHEMA_DATA_TYPES,
    defaultCriticality: "error",
  },
  // ── Arithmetic ───────────────────────────────────────────────
  {
    id: "non_negative",
    backendRuleType: "non_negative",
    scope: "column",
    title: "Cannot be negative",
    category: "Arithmetic",
    description: "Value must be >= 0.",
    expression: "[{col}] must be non-negative",
    applicableDataTypes: ["Integer", "Decimal"],
    defaultCriticality: "error",
  },
  {
    id: "sum_n_equals_total",
    backendRuleType: "sum_n_equals_total",
    scope: "schema",
    title: "Sum of Values",
    category: "Arithmetic",
    description:
      "Value of this field is equal to sum of one or more component fields within specified tolerance.",
    expression:
      "Values in [{total_column}] equal the sum of values in {components} within a tolerance of {tolerance}",
    applicableDataTypes: ["Integer", "Decimal"],
    defaultCriticality: "error",
  },
  // ── Chronological ────────────────────────────────────────────
  {
    id: "not_in_future",
    backendRuleType: "not_in_future",
    scope: "column",
    title: "Cannot be in the future",
    category: "Chronological",
    description: "Date must be today or earlier.",
    expression: "[{col}] must be on or before Present Day",
    applicableDataTypes: ["Date & Time"],
    defaultCriticality: "error",
  },
  {
    id: "before_other_date",
    backendRuleType: "before_other_date",
    scope: "schema",
    title: "Must be before date in another field",
    category: "Chronological",
    description: "Cross-field date order.",
    expression: "[{earlier_column}] must be before [{later_column}]",
    applicableDataTypes: ["Date & Time"],
    defaultCriticality: "error",
  },
  {
    id: "in_reference_list",
    backendRuleType: "in_reference_list",
    scope: "column",
    title: "Value must be in reference list",
    category: "Syntactical",
    description: "Restrict to a reference list.",
    expression: "[{col}] must be a value in reference list [{reference_list_id}]",
    applicableDataTypes: SCHEMA_DATA_TYPES,
    parametersSchema: {
      type: "object",
      required: ["reference_list_id"],
      properties: {
        reference_list_id: { type: "string", "x-ref": "reference_list" },
        case_sensitive: { type: "boolean", default: false },
      },
    },
    defaultCriticality: "error",
  },
  // ── If-Then ──────────────────────────────────────────────────
  {
    id: "if_x_positive_y_required",
    backendRuleType: "if_x_positive_y_required",
    scope: "schema",
    title: "If x > 0, y must not be null",
    category: "If-Then",
    description: "Conditional requirement.",
    expression: "If [{x_column}] > 0, [{y_column}] must not be null",
    applicableDataTypes: ["Integer", "Decimal"],
    defaultCriticality: "error",
  },
  {
    id: "if_x_negative_y_required",
    backendRuleType: "if_x_negative_y_required",
    scope: "schema",
    title: "If x < 0, y must not be null",
    category: "If-Then",
    description: "Conditional requirement.",
    expression: "If [{x_column}] < 0, [{y_column}] must not be null",
    applicableDataTypes: ["Integer", "Decimal"],
    defaultCriticality: "error",
  },
];

export const mockSchemaRules: SchemaRule[] = [
  {
    id: "rule-1",
    title: "Sum Equals Total",
    category: "Arithmetic",
    fieldCount: 6,
    ruleKey: "total_loss_reconciles_with_components",
    columnIds: ["field-total-loss"],
    formula: "[Total_Loss] = [Cost_1] + [Cost_2] + [Cost_3] + [Cost_4] + [Cost_5]",
    description:
      "Total loss has to be equal to the combined costs of various components of said loss.",
    hasConflict: false,
    presetId: "sum_n_equals_total",
    backendRuleType: "sum_n_equals_total",
    scope: "schema",
  },
  {
    id: "rule-2",
    title: "Must be before date in another field",
    category: "Chronological",
    fieldCount: 2,
    ruleKey: "inception_before_expiration",
    columnIds: ["field-inception-date", "field-expiration-date"],
    formula: "[Inception_Date] must be before [Expiration_Date]",
    description: "A claim's inception date must always occur before its expiration date.",
    hasConflict: false,
    presetId: "before_other_date",
    backendRuleType: "before_other_date",
    scope: "schema",
  },
  {
    id: "rule-3",
    title: "No Duplicate Values",
    category: "Syntactical",
    fieldCount: 1,
    ruleKey: "policy_number_no_duplicates",
    columnIds: ["field-policy-number"],
    formula: "[Policy_Number] must be a unique value",
    description: "Each policy number is different.",
    hasConflict: false,
    presetId: "column_unique",
    backendRuleType: "column_unique",
    scope: "column",
  },
  {
    id: "rule-4",
    title: "Value must be in reference list",
    category: "Syntactical",
    fieldCount: 1,
    ruleKey: "occupancy_description_value_must_be_in_reference_list",
    columnIds: ["field-occupancy-description"],
    formula:
      "[Occupancy_Description] must be a value in reference list [Occupancy Description Options]",
    description: "Restrict to a reference list.",
    hasConflict: false,
    presetId: "in_reference_list",
    backendRuleType: "in_reference_list",
    parameters: { reference_list_id: "reference-list-1", case_sensitive: false },
    scope: "column",
  },
];

export const mockReferenceLists = [
  {
    id: "reference-list-1",
    name: "Occupancy Description Options",
    usedInColumnIds: ["field-occupancy-description"],
    itemCount: 8,
    values: [
      "Tenanted",
      "Restaurant",
      "Office",
      "Seasonal",
      "Warehouse",
      "Buildings",
      "Manufacturing",
      "Other",
    ],
  },
];

export const mockReferenceListValues: Record<string, string[]> = {
  "Occupancy Description Options": [
    "Tenanted",
    "Restaurant",
    "Office",
    "Seasonal",
    "Warehouse",
    "Buildings",
    "Manufacturing",
    "Other",
  ],
};

export const mockSchemaActivity: SchemaActivityEvent[] = [
  {
    id: "act-1",
    type: "draft_saved",
    actor: "Bharti Devi",
    timestamp: "2026-02-02T14:34:00Z",
  },
  {
    id: "act-2",
    type: "column_edited",
    actor: "Bharti Devi",
    timestamp: "2026-02-02T14:20:00Z",
    detail: "Policy_Number",
  },
  {
    id: "act-3",
    type: "column_added",
    actor: "Admin User",
    timestamp: "2026-02-02T11:05:00Z",
    detail: "Claim_Status",
  },
  {
    id: "act-4",
    type: "rule_added",
    actor: "Bharti Devi",
    timestamp: "2026-01-28T15:48:00Z",
    detail: "No Duplicate Values",
  },
  {
    id: "act-5",
    type: "column_deleted",
    actor: "Admin User",
    timestamp: "2026-01-28T15:30:00Z",
    detail: "Legacy_ID",
  },
  {
    id: "act-6",
    type: "column_edited",
    actor: "Bharti Devi",
    timestamp: "2026-01-28T09:15:00Z",
    detail: "Total_Loss",
  },
  {
    id: "act-7",
    type: "draft_saved",
    actor: "Bharti Devi",
    timestamp: "2026-01-20T16:00:00Z",
  },
  {
    id: "act-8",
    type: "rule_added",
    actor: "Bharti Devi",
    timestamp: "2026-01-20T15:45:00Z",
    detail: "Before Other Date",
  },
  {
    id: "act-9",
    type: "column_added",
    actor: "Bharti Devi",
    timestamp: "2026-01-20T14:30:00Z",
    detail: "Inception_Date",
  },
  {
    id: "act-10",
    type: "column_added",
    actor: "Bharti Devi",
    timestamp: "2026-01-20T14:10:00Z",
    detail: "Total_Loss",
  },
  {
    id: "act-11",
    type: "schema_created",
    actor: "Bharti Devi",
    timestamp: "2026-01-13T10:00:00Z",
  },
];

export const mockSchemaTemplates: SchemaTemplate[] = [
  {
    id: "template-lloyds-52",
    name: "Lloyd's 5.2 Standard",
    description:
      "Cover holder reporting standard. Insert longer description of the schema and its use-cases here so that the user has more context for whether this is the right choice for their organization.",
    status: "preset",
    totalColumns: 23,
    rules: 61,
    crossColumnRules: 9,
    knownMappings: 31,
  },
  {
    id: "template-cover-holder",
    name: "Cover Holder Standard",
    description:
      "Cover holder reporting standard. Insert longer description of the schema and its use-cases here so that the user has more context for whether this is the right choice for their organization.",
    status: "preset",
    totalColumns: 84,
    rules: 70,
    crossColumnRules: 32,
    knownMappings: 250,
  },
  {
    id: "template-claims",
    name: "Claims Template",
    description:
      "Cover holder reporting standard. Insert longer description of the schema and its use-cases here so that the user has more context for whether this is the right choice for their organization.",
    status: "preset",
    totalColumns: 84,
    rules: 70,
    crossColumnRules: 32,
    knownMappings: 250,
  },
];
